/**
 * Pure helpers for product-level optimization insights.
 *
 * Extracted from FunnelAnalytics so the math can be unit-tested. The headline
 * bug these guard against: an unweighted mean of per-product conversion rates
 * lets a tiny-sample product (e.g. 1 visitor / 1 sale = 100%) pull the
 * "average" above every real product, producing an impossible baseline.
 */

export interface ProductPerf {
  product_id?: string
  product_name?: string
  views?: number
  conversions?: number
  unique_visitors?: number
  conversion_rate?: number
}

/** Visitors a rate should be measured against: unique visitors, falling back to
 * raw views only when unique_visitors isn't serialized. conversion_rate upstream
 * is conversions / unique_visitors, so this keeps the denominator consistent. */
export function visitorsOf(p: ProductPerf): number {
  return p.unique_visitors ?? p.views ?? 0
}

/**
 * Traffic-weighted average conversion rate (%) across products with enough
 * sample. Weighting by visitors guarantees the result stays within the range of
 * actual per-product rates — it can never exceed the best product.
 */
export function weightedAvgConversion(
  products: ProductPerf[],
  minVisitors = 20
): number {
  const eligible = products.filter(p => visitorsOf(p) >= minVisitors)
  const visitors = eligible.reduce((sum, p) => sum + visitorsOf(p), 0)
  const conversions = eligible.reduce((sum, p) => sum + (p.conversions || 0), 0)
  return visitors > 0 ? (conversions / visitors) * 100 : 0
}

export interface FunnelStepPerf {
  users?: number
  progressionRate?: number
  sessions_progressed?: number
}

/**
 * Sessions that did NOT progress from a step, derived from the step's OWN
 * progression data. Never subtract a later step's session count to get this:
 * funnel steps aren't nested subsets (sessions can reach later steps via other
 * paths), so cross-step subtraction can go negative (the "-19 no completan"
 * bug). Result is clamped to >= 0.
 */
export function stepNonCompleters(step: FunnelStepPerf): number {
  const sessions = step.users || 0
  const completed = step.sessions_progressed
    ?? Math.round(sessions * ((step.progressionRate || 0) / 100))
  return Math.max(0, sessions - completed)
}
